import {
    collection,
    doc,
    DocumentData,
    DocumentReference,
    runTransaction,
    serverTimestamp,
} from "firebase/firestore";
import {appFirestore} from "../../../../index";
import {CALENDAR_CHANGE_LOG_COLLECTION} from "./CalendarChangeLogType";
import {asCalendarDate, formatCalendarDeletionSummary, getCalendarFieldChanges} from "./calendarChangeDiff";
import {updateCalendarPlan} from "../../../calendar/scheduleMutations";

export type CalendarChangeActor = {
    id?: string;
    name?: string;
};

export type CalendarChangeContext = {
    eventNumber?: string;
    eventName?: string;
};

export type CalendarItemUpdate = {
    ref: DocumentReference<unknown>;
    data: DocumentData;
    context?: CalendarChangeContext;
};

function actorData(actor: CalendarChangeActor | null | undefined): {
    changedByEmployeeId: string | null;
    changedByName: string;
} {
    return {
        changedByEmployeeId: actor?.id ?? null,
        changedByName: actor?.name ?? "Неизвестен потребител",
    };
}

function contextData(data: DocumentData, context?: CalendarChangeContext) {
    const eventNumber = context?.eventNumber ?? (typeof data.eventId === "string" ? data.eventId : "");

    return {
        department: typeof data.department === "string" ? data.department : "montage",
        eventId: typeof data.eventId === "string" ? data.eventId : "",
        eventNumber: eventNumber.trim(),
        eventName: context?.eventName ?? "",
        employeeId: typeof data.employeeId === "string" ? data.employeeId : null,
        employeeName: typeof data.employeeName === "string" ? data.employeeName : "",
        periodStart: asCalendarDate(data.eventDayStart),
        periodEnd: asCalendarDate(data.eventDayEnd),
    };
}

/** Записва редакцията и нейния лог атомарно, без да създава лог за първоначално попълване. */
export async function updateCalendarItemsWithChangeLog(
    updates: CalendarItemUpdate[],
    actor: CalendarChangeActor | null | undefined
): Promise<void> {
    if (updates.length === 0) return;
    // Планирането минава през сървърната транзакция с периода на събитието.
    if (updates.some(update => ["eventDayStart", "eventId", "dayType", "employeeId", "department"].some(key => key in update.data))) {
        for (const update of updates) await updateCalendarPlan(update.ref.id, update.data);
        return;
    }
    const operationId = doc(collection(appFirestore, CALENDAR_CHANGE_LOG_COLLECTION)).id;

    await runTransaction(appFirestore, async transaction => {
        const refs = updates.map(update => update.ref as DocumentReference<DocumentData>);
        const snapshots = await Promise.all(refs.map(ref => transaction.get(ref)));
        snapshots.forEach((snapshot, index) => {
            if (!snapshot.exists()) throw new Error("Календарният запис вече не съществува.");
            const update = updates[index];
            const current = snapshot.data();
            const merged = {...current, ...update.data};
            const changes = getCalendarFieldChanges(current, update.data);

            transaction.update(refs[index], update.data);
            changes.forEach(change => {
                const logRef = doc(
                    appFirestore,
                    CALENDAR_CHANGE_LOG_COLLECTION,
                    `${operationId}_${index}_${change.changeType}`
                );
                transaction.set(logRef, {
                    ...change,
                    ...contextData(merged, update.context),
                    operationId,
                    calendarItemId: snapshot.id,
                    changedAt: serverTimestamp(),
                    ...actorData(actor),
                });
            });
        });
    });
}

/** Изтриването и неизменяемият лог се записват в една транзакция. */
export async function deleteCalendarItemsWithChangeLog(
    items: Array<{ref: DocumentReference<unknown>; context?: CalendarChangeContext}>,
    actor: CalendarChangeActor | null | undefined
): Promise<void> {
    if (items.length === 0) return;
    const operationId = doc(collection(appFirestore, CALENDAR_CHANGE_LOG_COLLECTION)).id;

    // Един изтрит документ и неговият лог са две операции; 200 записа остават
    // безопасно под лимита от 500 записа за Firestore транзакция.
    for (let offset = 0; offset < items.length; offset += 200) {
        const chunk = items.slice(offset, offset + 200);
        await runTransaction(appFirestore, async transaction => {
            const refs = chunk.map(item => item.ref as DocumentReference<DocumentData>);
            const snapshots = await Promise.all(refs.map(ref => transaction.get(ref)));
            snapshots.forEach((snapshot, index) => {
                if (!snapshot.exists()) return;
                const item = chunk[index];
                const data = snapshot.data();
                // Детерминираният ID позволява сървърният onDelete fallback да
                // разпознае атомарно записан лог и да не създаде дубликат.
                const logRef = doc(
                    appFirestore,
                    CALENDAR_CHANGE_LOG_COLLECTION,
                    `calendar-delete-${snapshot.id}`
                );

                transaction.delete(refs[index]);
                transaction.set(logRef, {
                    changeType: "DELETE",
                    operationId,
                    calendarItemId: snapshot.id,
                    ...contextData(data, item.context),
                    previousValue: formatCalendarDeletionSummary(data, item.context?.eventName),
                    newValue: "Записът е изтрит",
                    changedAt: serverTimestamp(),
                    ...actorData(actor),
                });
            });
        });
    }
}
